import { Link } from 'react-router-dom';
import { DesktopMenu } from '../App';
import { useState } from 'react';

export default function FAQs() {
  return (
    <>
      <DesktopMenu />

      <div className="text-secondary bg-linen font-archer text-[20px] p-8">
        <div className="flex flex-col justify-center max-w-[900px] mx-auto gap-4">
          <div className="text-center mb-4">
            <div className="text-[36px] font-semibold">FAQs</div>
            <div className="text-xl">(if you have a question that you do not see answered below, please reach out to us!)</div>
          </div>

          <Accordion title="Do I need a passport?">
            <ul className="list-disc pl-8">
              <li>
                <div>YES! You will need a passport to travel to the UK.</div>
                <ul className="list-[circle] pl-8">
                  <li>For US citizens traveling to the UK, a US passport must be valid for the duration of their stay. Additionally, it's recommended that passports are valid for at least six months beyond the planned return date.</li>
                </ul>
              </li>
            </ul>
            </Accordion>

          <Accordion title="In addition to my passport, do I need anything else to enter the UK?">
            <ul className="list-disc pl-8">
              <li>
                <div>​​YES! You will need an electronic travel authorization (ETA) to visit the UK. <a href="https://www.gov.uk/eta" target="_blank" rel="noreferrer" className="underline text-primary">Please visit this site for more details.</a></div>
                <ul className="list-[circle] pl-8">
                  <li>
                  More information can also be found on that site regarding other visas for non-US citizens.
                  </li>
                </ul>
              </li>
            </ul>
          </Accordion>
          
          <Accordion title="What airport do you recommend flying into?">
            <ul className="list-disc pl-8">
              <li>
              We recommend arriving at <mark className="bg-secondary/20 font-bold text-secondary">Heathrow Airport (LHR).</mark>
              </li>
            </ul>
          </Accordion>

          <Accordion title="Do you have any recommendations on how to get around?">
            <ul className="list-disc pl-8">
              <li>
                <div>​​The nearest major city to the ceremony location is Bristol and we highly recommend getting a rental car to travel from LHR to Bristol. Alternatively, please do your research on other methods of transportation (bus/train/Uber/taxi) and make the necessary arrangements.</div>
                <ul className="list-[circle] pl-8">
                  <li>
                  If booking a rental car, look into which companies operate out of LHR so you can pick up your vehicle after landing. We used Hertz on our last trip and had a good experience!
                  </li>
                  <li>
                  US citizens do not need an International Driving Permit (IDP) if they have a valid US driver's license. <a href="https://www.gov.uk/driving-nongb-licence" target="_blank" rel="noreferrer" className="underline text-primary">You can find out more here.</a>
                  </li>
                </ul>
              </li>
              <li>
              Once in London, we recommend returning your rental car and mainly using London buses (yes, those red double deckers!) and the London Underground, also known as the Tube.
              </li>
            </ul>
          </Accordion>

          <Accordion title="Where is the ceremony and dinner reception located and what is the dress code?">
            <ul className="list-disc pl-8">
              <li>
              All of this information can be found on the <Link to="/event-details" className="underline text-primary">Event Details</Link> page. 
              </li>
            </ul>
          </Accordion>

          <Accordion title="Is the ceremony and dinner reception indoors or outdoors?">
            <ul className="list-disc pl-8">
              <li>
              Both the ceremony and dinner reception will be indoors. UK weather can be unpredictable sometimes thus we recommend having an umbrella handy for any strolls to and from the venue.
              </li>
            </ul>
          </Accordion>

          <Accordion title="Can I take photos throughout the ceremony and reception?">
            <ul className="list-disc pl-8">
              <li>
              ​We will be having an unplugged ceremony and ask that you turn off or silence all devices. However, once the ceremony ends, feel free to take as many photos as you wish, especially during the dinner reception.
              </li>
            </ul>
          </Accordion>

          <Accordion title="Do you have a registry?">
            <ul className="list-disc pl-8">
              <li>
                ​​No, we do not have a wedding registry. Your attendance to this event means more to us than any gift!
              </li>
            </ul>
          </Accordion>

          <Accordion title="What if I can't make it?">
            <ul className="list-disc pl-8">
              <li>
              ​We'll be sad and miss you but given that our wedding requires travel, money, and time, we completely understand if you are unable to attend. We only ask that you let us know as soon as possible or by June 2026.
              </li>
            </ul>
          </Accordion>

          <Accordion title="Can you share more of your own travel itinerary for this trip?">
            <ul className="list-disc pl-8">
              <li>
                You can find our itinerary linked <a className="underline" href="https://docs.google.com/spreadsheets/d/15uJyykVkvgAC5fwy-MQKjHhLsYo-oBC44dW6wNYNI_8/edit?usp=sharing" target="_blank" rel="noreferrer">here</a>!
              </li>
            </ul>
          </Accordion>

          <Accordion title="Where will wedding updates be posted?">
            <ul className="list-disc pl-8">
              <li>
              ​Wedding updates will be posted on this website and we will be texting our guests whenever we add important updates!
              </li>
            </ul>
          </Accordion>

          <div className="mt-4">
            <div className="text-[22px] font-semibold underline">Other miscellaneous details:</div>
            <ul className="list-disc pl-8">
              <li>
              The timezone will be British Summer Time (BST).
              </li>
              <li>
              Currency is British Pound (£) in the UK. We recommend bringing some cash just in case.
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="pt-12 hidden md:block">
        <DesktopMenu />
      </div>
    </>
  );
};

function Accordion({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className={`
        border border-secondary rounded-md
        transition-all duration-300 ease-in-out
        shadow-[0px_0px_0_rgb(209,117,132)] 
        hover:shadow-[5px_5px_0_rgb(209,117,132)]
      `}
    >
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="w-full p-4 flex justify-between items-center text-left gap-4"
      >
        <span className="text-[22px] font-bold">{title}</span>
        <span
          className={`transition-transform duration-300 text-[16px] ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          ▼
        </span>
      </button>

      <div
        className={`
          overflow-hidden transition-all duration-300 ease-in-out
          ${isOpen ? "max-h-[1000px] opacity-100 translate-y-0" : "max-h-0 opacity-0 -translate-y-1"}
        `}
      >
        <div className="p-4 pt-0">{children}</div>
      </div>
    </div>
  );
}